import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OmnichannelOrder, ChannelId } from 'services/order/order.type';
import { computePrice } from 'utils/price';
import { Text } from 'components';
import { formatOrderDate, getDatePartFromDate, getTimePartFromDate } from 'pages/Order/utils';
import { useCustomNavigate } from 'hooks/useCustomNavigate';
import {
    ActionContainer,
    OrderDetailsContainer,
    OrderPrice,
    StyledOrderListItem,
    StyledSvyIcon,
    UnavailableDetails,
} from './OrderItem.style';

export const OrderItem: React.FunctionComponent<{ order: OmnichannelOrder }> = ({ order }) => {
    const { t, i18n } = useTranslation();
    const { navigateWithParams } = useCustomNavigate();

    const isWebOrKioskOrder = useMemo(() => {
        return (
            order.channelId.toString() === ChannelId.WEB.toString() ||
            order.channelId.toString() === ChannelId.KIOSK.toString()
        );
    }, [order]);

    const isContraEntry = useMemo(() => {
        return order.status === 'CONTRA_ENTRY';
    }, [order]);

    const textDescription = useMemo(() => {
        if (isContraEntry && order.reportOrderContraEntry) {
            return `${t('orders_history.order_cancellation')} - ${formatOrderDate(
                order.reportOrderContraEntry?.originOrderDate,
            )}`;
        }
        return `${order.restaurant.name} - ${getTimePartFromDate(order.startDate)}`;
    }, [isContraEntry, order, t]);

    const linkProps = useMemo(() => {
        if (isWebOrKioskOrder && !isContraEntry)
            return { role: 'link', onClick: () => navigateWithParams(order.omnichannelOrderId) };
        return {};
    }, [isWebOrKioskOrder, order, navigateWithParams, isContraEntry]);

    return (
        <li>
            <StyledOrderListItem {...linkProps}>
                <OrderDetailsContainer>
                    <Text
                        noTranslation
                        text={getDatePartFromDate(order.startDate)}
                        type={2}
                        weight="Bold"
                        data-testid="order-date"
                    />
                    <br />
                    <Text
                        text={textDescription}
                        noTranslation
                        type={3}
                        color="secondaryTextColor" // TODO: check if this is the correct color
                        data-testid="restaurant-name"
                    />
                    <OrderPrice
                        label={computePrice(
                            i18n,
                            Math.abs(order.totalPriceDiscountedWithTaxIncluded),
                            order.currency,
                        )}
                        data-testid="table-number"
                        color={isContraEntry ? 'warning' : 'primary'}
                    />
                </OrderDetailsContainer>
                {!isContraEntry && (
                    <ActionContainer>
                        {isWebOrKioskOrder ? (
                            <StyledSvyIcon
                                icon="arrow-right-s-line"
                                data-testid="go-to-order-details"
                            />
                        ) : (
                            <UnavailableDetails
                                label={t('orders_history.details_unavailable')}
                                color="neutral"
                                data-testid="unavailable-details"
                            />
                        )}
                    </ActionContainer>
                )}
            </StyledOrderListItem>
        </li>
    );
};
